/**
 * Account Recovery Service
 * 
 * Service for recovering soft-deleted accounts within the 30-day recovery window
 */

import { supabase } from '../lib/supabase';
import type { SuccessResponse, ErrorResponse } from '../types/security.types';

export const accountRecoveryService = {
  /**
   * Check if account is soft-deleted and recoverable
   */
  async checkAccountStatus(userId: string): Promise<{
    data?: {
      isDeleted: boolean;
      deletedAt: Date | null;
      deletionScheduledAt: Date | null;
      canRecover: boolean;
    };
    error?: ErrorResponse;
  }> {
    try {
      const { data, error } = await supabase
        .from('account_deletion_queue')
        .select('deleted_at, deletion_scheduled_at, recovered_at, permanently_deleted_at')
        .eq('user_id', userId)
        .maybeSingle() as { data: any; error: any };

      if (error) {
        // Handle 406 (Not Acceptable) and other errors gracefully
        // 406 can occur if the table doesn't exist, RLS blocks access, or content negotiation fails
        if (error.code === 'PGRST116' || error.status === 406 || error.code === '42P01') {
          // No deletion record found or table not accessible - account is active
          return {
            data: {
              isDeleted: false,
              deletedAt: null,
              deletionScheduledAt: null,
              canRecover: false,
            }
          };
        }
        // Log but don't fail - account status check is not critical for account operations
        console.warn('Error checking account deletion status:', error);
        return {
          data: {
            isDeleted: false,
            deletedAt: null,
            deletionScheduledAt: null,
            canRecover: false,
          }
        };
      }

      if (!data) {
        return {
          data: {
            isDeleted: false,
            deletedAt: null,
            deletionScheduledAt: null,
            canRecover: false,
          }
        };
      }

      const isDeleted = !!(data && !data.recovered_at && !data.permanently_deleted_at);
      const deletedAt = data.deleted_at ? new Date(data.deleted_at) : null;
      const deletionScheduledAt = data.deletion_scheduled_at ? new Date(data.deletion_scheduled_at) : null;
      const now = new Date();
      const canRecover = isDeleted && !!deletionScheduledAt && now < deletionScheduledAt;

      return {
        data: {
          isDeleted,
          deletedAt,
          deletionScheduledAt,
          canRecover,
        }
      };
    } catch (error: any) {
      // Handle all errors gracefully - account status check should never block operations
      console.warn('Error checking account deletion status:', error);
      // Return "account is active" as safe default
      return {
        data: {
          isDeleted: false,
          deletedAt: null,
          deletionScheduledAt: null,
          canRecover: false,
        }
      };
    }
  },

  /**
   * Recover a soft-deleted account
   */
  async recoverAccount(userId: string): Promise<SuccessResponse> {
    try {
      // Check if account can be recovered
      const statusResult = await this.checkAccountStatus(userId);
      if (statusResult.error) {
        return {
          error: statusResult.error
        };
      }

      if (!statusResult.data?.canRecover) {
        return {
          error: {
            code: 'CANNOT_RECOVER',
            message: 'Account cannot be recovered. It may have been permanently deleted or the recovery window has expired.'
          }
        };
      }

      // Mark account as recovered
      // Type assertion needed because account_deletion_queue is not in Database types yet
      const supabaseAny = supabase as any;
      const { error: updateError } = await supabaseAny
        .from('account_deletion_queue')
        .update({ recovered_at: new Date().toISOString() })
        .eq('user_id', userId)
        .is('recovered_at', null)
        .is('permanently_deleted_at', null);

      if (updateError) {
        return {
          error: {
            code: 'UNKNOWN_ERROR',
            message: updateError.message || 'Failed to recover account'
          }
        };
      }

      return {
        data: {
          success: true
        }
      };
    } catch (error: any) {
      console.error('Error recovering account:', error);
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message || 'Failed to recover account'
        }
      };
    }
  },
};

